import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ExpiryBadge } from "@/components/common/ExpiryBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ImportDialog } from "@/components/common/ImportDialog";
import { AddStockDialog } from "@/components/inventory/AddStockDialog";
import { FG_OPENING_IMPORT } from "@/config/imports";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtNumber } from "@/lib/format";
import { isDispatchable } from "@/lib/production";
import { Boxes, Search, ScrollText, FileSpreadsheet, PackagePlus } from "lucide-react";

const num = (x) => Number(x) || 0;

export default function FinishedGoods() {
  const { role } = useAuth();
  const canAdd = ["super_admin", "stores_manager", "store_executive"].includes(role);
  const { data, isLoading, isError, error, refetch } = useResource("/finished-goods");
  const { data: ledgerData } = useResource("/finished-goods/ledger");
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const ledger = useMemo(() => (Array.isArray(ledgerData) ? ledgerData : []), [ledgerData]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [ledgerFor, setLedgerFor] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [addFor, setAddFor] = useState(undefined);

  const statuses = useMemo(() => ["All", ...Array.from(new Set(rows.map((r) => r.status).filter(Boolean)))], [rows]);
  const filtered = useMemo(() => rows
    .filter((r) => status === "All" || r.status === status)
    .filter((r) => !q || `${r.sku || ""} ${r.productName || ""}`.toLowerCase().includes(q.toLowerCase())), [rows, q, status]);

  const skuLedger = ledger.filter((l) => l.sku === ledgerFor?.sku);

  return (
    <div className="space-y-6">
      <PageHeader title="Finished Goods Inventory" subtitle="Ledger-driven FG stock. Only SALEABLE quantity is available for normal dispatch.">
        {canAdd && (
          <Button data-testid="fg-add-stock" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => setAddFor(null)}>
            <PackagePlus className="h-4 w-4" /> Add Stock
          </Button>
        )}
        <Button data-testid="btn-import-fg-opening" variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
          <FileSpreadsheet className="h-4 w-4" /> Import Opening Stock
        </Button>
      </PageHeader>

      <div className="flex items-start gap-3 rounded-lg border border-stone-200 bg-stone-50/70 px-4 py-3 text-xs text-stone-600">
        <Boxes className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <span><strong>FG balance</strong> = Opening + Production + Returns + Adjustments − Dispatch − Damage − Expiry &nbsp;·&nbsp; <strong>Available</strong> = Current − Reserved. Balances are never overwritten; every movement is a ledger entry.</span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {statuses.map((s) => (
            <Button key={s} size="sm" variant={status === s ? "default" : "outline"}
              data-testid={`fg-filter-${s.toLowerCase()}`}
              className={status === s ? "h-9 bg-stone-800 hover:bg-stone-900" : "h-9"} onClick={() => setStatus(s)}>{s}</Button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input data-testid="fg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search SKU / product…" className="h-9 pl-8 text-sm" />
        </div>
      </div>

      {isLoading ? <LoadingState label="Loading finished goods…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? <EmptyState title="No finished goods in stock" message="FG appears here after production is completed and posted to the FG ledger." /> : (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <table className="w-full text-sm">
              <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                <tr>
                  <th className="px-4 py-2.5">SKU</th><th className="px-4 py-2.5">Product</th>
                  <th className="px-4 py-2.5 text-right">Current</th><th className="px-4 py-2.5 text-right">Reserved</th>
                  <th className="px-4 py-2.5 text-right">Available</th><th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Expiry</th><th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filtered.map((r) => {
                  const current = num(r.total ?? r.current ?? r.currentStock);
                  const available = r.available !== undefined ? num(r.available) : current - num(r.reserved);
                  return (
                    <tr key={r.sku} data-testid={`fg-row-${r.sku}`} className="hover:bg-amber-50/30">
                      <td className="px-4 py-2.5 font-mono text-xs text-stone-700">{r.sku}</td>
                      <td className="px-4 py-2.5 text-stone-700">{r.productName}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtNumber(current)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-amber-700">{fmtNumber(num(r.reserved))}</td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${isDispatchable(r.status) ? "text-emerald-700" : "text-stone-400"}`}>{fmtNumber(available)}</td>
                      <td className="px-4 py-2.5"><StatusBadge value={r.status} /></td>
                      <td className="px-4 py-2.5">
                        {(r.expiryDate || r.nearestExpiry)
                          ? <div className="flex items-center gap-2"><span>{fmtDate(r.expiryDate || r.nearestExpiry)}</span><ExpiryBadge expiryDate={r.expiryDate || r.nearestExpiry} testId={`fg-expiry-badge-${r.sku}`} /></div>
                          : <ExpiryBadge expiryDate={null} testId={`fg-expiry-badge-${r.sku}`} />}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button data-testid={`fg-ledger-${r.sku}`} size="sm" variant="ghost" className="h-8 gap-1.5 text-xs" onClick={() => setLedgerFor(r)}>
                          <ScrollText className="h-3.5 w-3.5" /> Ledger
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      <Sheet open={!!ledgerFor} onOpenChange={(o) => !o && setLedgerFor(null)}>
        <SheetContent className="w-full overflow-y-auto bg-white sm:max-w-xl">
          {ledgerFor && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display">FG Ledger — {ledgerFor.sku}</SheetTitle>
                <SheetDescription className="text-xs">{ledgerFor.productName} · every movement that drives the balance</SheetDescription>
              </SheetHeader>
              <div className="mt-5">
                {skuLedger.length === 0 ? (
                  <EmptyState title="No ledger movements" message="This SKU has no recorded FG ledger entries yet." />
                ) : (
                  <div className="overflow-hidden rounded-lg border border-stone-200">
                    <table className="w-full text-xs">
                      <thead className="border-b border-stone-200 bg-stone-50 text-left font-semibold text-stone-500">
                        <tr><th className="px-3 py-2">When</th><th className="px-3 py-2">Type</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Batch</th></tr>
                      </thead>
                      <tbody className="divide-y divide-stone-100">
                        {skuLedger.map((l, i) => (
                          <tr key={i}>
                            <td className="px-3 py-2 text-stone-500">{fmtDateTime(l.createdAt || l.date)}</td>
                            <td className="px-3 py-2"><StatusBadge value={l.type || l.movementType} /></td>
                            <td className={`px-3 py-2 text-right font-mono ${num(l.qty ?? l.quantity) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmtNumber(l.qty ?? l.quantity)}</td>
                            <td className="px-3 py-2 font-mono text-stone-500">{l.batchNo || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} config={FG_OPENING_IMPORT} onDone={refetch} />
      <AddStockDialog open={addFor !== undefined} onOpenChange={(o) => !o && setAddFor(undefined)} mode="fg" item={addFor} onDone={refetch} />
    </div>
  );
}
